import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, Bot } from "lucide-react";
import { format, parseISO } from "date-fns";

interface Invoice {
  id: string;
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  status: string;
  apr_rate: number;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  email_sent_at: string | null;
  email_error: string | null;
  created_at: string;
}

interface ActivityEvent {
  id: string;
  timestamp: string;
  type:
    | "apr_change"
    | "budget_change"
    | "email_sent"
    | "email_failed"
    | "supplier_accepted"
    | "supplier_declined"
    | "invoice_uploaded"
    | "ach_generated";
  title: string;
  description: string;
  actor: "user" | "discorun";
  meta?: Record<string, string | number>;
  amount?: string;
  supplier?: string;
}

interface ActivityLogProps {
  invoices: Invoice[];
}

const aprHistory = [
  { date: "2026-02-09", rate: 12, changedBy: "Buyer" },
  { date: "2026-02-05", rate: 15, changedBy: "Buyer" },
  { date: "2026-01-28", rate: 10, changedBy: "Buyer" },
  { date: "2026-01-15", rate: 8, changedBy: "Sarah Chen" },
  { date: "2026-01-02", rate: 12, changedBy: "Buyer" },
  { date: "2025-12-20", rate: 14, changedBy: "Buyer" },
  { date: "2025-12-05", rate: 10, changedBy: "Sarah Chen" },
];

const budgetHistory = [
  { date: "2026-02-01", amount: 100000, changedBy: "System", reason: "Monthly budget refresh" },
  {
    date: "2026-01-15",
    amount: 150000,
    changedBy: "Buyer",
    reason: "Increased to cover Q1 demand",
  },
  { date: "2026-01-01", amount: 100000, changedBy: "System", reason: "Monthly budget refresh" },
  { date: "2025-12-10", amount: 75000, changedBy: "Buyer", reason: "Initial budget set" },
];

function generateActivityEvents(invoices: Invoice[]): ActivityEvent[] {
  const events: ActivityEvent[] = [];

  // APR changes
  aprHistory.forEach((entry, idx) => {
    const prev = idx < aprHistory.length - 1 ? aprHistory[idx + 1].rate : null;
    const direction = prev !== null ? (entry.rate > prev ? "increased" : "decreased") : "set";
    events.push({
      id: `apr-${idx}`,
      timestamp: `${entry.date}T10:00:00Z`,
      type: "apr_change",
      title: `APR ${direction} to ${entry.rate}%`,
      description:
        prev !== null
          ? `Rate changed from ${prev}% to ${entry.rate}% by ${entry.changedBy}. ${entry.rate > 15 ? "Higher rates increase discount cost but improve supplier acceptance." : entry.rate < 8 ? "Lower rates reduce cost but may decrease supplier participation." : "Rate is within the optimal 8-20% range for supplier acceptance."}`
          : `Initial rate set to ${entry.rate}% by ${entry.changedBy}.`,
      actor: entry.changedBy === "Buyer" || entry.changedBy === "Sarah Chen" ? "user" : "discorun",
    });
  });

  // Budget changes
  budgetHistory.forEach((entry, idx) => {
    events.push({
      id: `budget-${idx}`,
      timestamp: `${entry.date}T09:00:00Z`,
      type: "budget_change",
      title: `Budget set to $${(entry.amount / 1000).toFixed(0)}K`,
      description: `${entry.reason}. Changed by ${entry.changedBy}.`,
      actor: entry.changedBy === "System" ? "discorun" : "user",
      meta: { amount: entry.amount },
    });
  });

  // Invoice-related events
  invoices.forEach((inv) => {
    // Email sent
    if (inv.email_sent_at) {
      const daysEarly = Math.max(
        0,
        Math.floor(
          (new Date(inv.payment_due_date).getTime() - new Date(inv.email_sent_at).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      );
      events.push({
        id: `email-${inv.id}`,
        timestamp: inv.email_sent_at,
        type: "email_sent",
        title: `Offer sent to ${inv.supplier_name}`,
        description: `DiscoRun sent an early payment offer for $${inv.payment_amount.toLocaleString()} at ${inv.apr_rate}% APR. Supplier would receive $${(inv.discounted_payment_amount || inv.payment_amount).toLocaleString()} — ${daysEarly} days before due date. This saves the buyer $${(inv.discount_amount || 0).toLocaleString()}.`,
        actor: "discorun",
        amount: `$${inv.payment_amount.toLocaleString()}`,
        supplier: inv.supplier_name,
      });
    }

    // Email failed
    if (inv.email_error) {
      events.push({
        id: `err-${inv.id}`,
        timestamp: inv.created_at,
        type: "email_failed",
        title: `Offer blocked for ${inv.supplier_name}`,
        description: `${inv.email_error}. Invoice amount: $${inv.payment_amount.toLocaleString()}. ${inv.email_error.includes("budget") ? "Increase budget to automatically retry." : "Review and retry manually."}`,
        actor: "discorun",
        supplier: inv.supplier_name,
      });
    }

    // Supplier accepted
    if (inv.status === "early_payment_scheduled" || inv.status === "early_payment_completed") {
      const actionDate = inv.email_sent_at
        ? new Date(
            new Date(inv.email_sent_at).getTime() + Math.random() * 86400000 * 2
          ).toISOString()
        : inv.created_at;
      events.push({
        id: `accept-${inv.id}`,
        timestamp: actionDate,
        type: "supplier_accepted",
        title: `${inv.supplier_name} accepted`,
        description: `Supplier accepted early payment of $${(inv.discounted_payment_amount || inv.payment_amount).toLocaleString()} (saving $${(inv.discount_amount || 0).toLocaleString()} at ${inv.apr_rate}% APR). ${inv.status === "early_payment_completed" ? "Payment has been processed via ACH." : "Ready for next ACH payment run."}`,
        actor: "discorun",
        amount: `$${(inv.discounted_payment_amount || inv.payment_amount).toLocaleString()}`,
        supplier: inv.supplier_name,
      });
    }

    // Supplier declined
    if (inv.status === "standard_payment") {
      events.push({
        id: `decline-${inv.id}`,
        timestamp: inv.created_at,
        type: "supplier_declined",
        title: `${inv.supplier_name} declined`,
        description: `Supplier did not accept the early payment offer of $${(inv.discounted_payment_amount || inv.payment_amount).toLocaleString()} at ${inv.apr_rate}% APR. Invoice will be paid at standard terms ($${inv.payment_amount.toLocaleString()}).`,
        actor: "discorun",
        supplier: inv.supplier_name,
      });
    }
  });

  return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

type FilterType =
  | "all"
  | "apr_change"
  | "budget_change"
  | "email_sent"
  | "supplier_accepted"
  | "supplier_declined"
  | "email_failed";

const filterOptions: { value: FilterType; label: string }[] = [
  { value: "all", label: "All" },
  { value: "email_sent", label: "Outreach" },
  { value: "supplier_accepted", label: "Accepted" },
  { value: "supplier_declined", label: "Declined" },
  { value: "apr_change", label: "APR" },
  { value: "budget_change", label: "Budget" },
  { value: "email_failed", label: "Errors" },
];

function getDotClass(type: string): string {
  switch (type) {
    case "supplier_accepted":
    case "ach_generated":
      return "bg-emerald-400 ring-2 ring-emerald-400/20 animate-pulse";
    case "email_sent":
    case "apr_change":
    case "invoice_uploaded":
      return "bg-indigo-400";
    case "supplier_declined":
    case "email_failed":
      return "bg-red-400/70";
    default:
      return "bg-amber-400";
  }
}

export function ActivityLog({ invoices }: ActivityLogProps) {
  const [filter, setFilter] = useState<FilterType>("all");
  const events = generateActivityEvents(invoices);
  const filtered = filter === "all" ? events : events.filter((e) => e.type === filter);

  const formatTime = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMM d, h:mm a");
    } catch {
      return dateString;
    }
  };

  const formatDayHeader = (dateString: string) => {
    try {
      return format(parseISO(dateString), "EEEE, MMM d");
    } catch {
      return dateString;
    }
  };

  // Group by day
  const grouped: Record<string, ActivityEvent[]> = {};
  filtered.forEach((event) => {
    const day = event.timestamp.split("T")[0];
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(event);
  });

  return (
    <div
      className="rounded-xl overflow-hidden h-fit"
      style={{
        background: "hsl(224 40% 7%)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.4)",
      }}
    >
      <div className="p-4 border-b border-white/[0.06]">
        <h2 className="text-base font-display font-semibold text-white/90 flex items-center gap-2 tracking-tight">
          <Clock className="w-4 h-4 text-indigo-400" />
          Activity Log
        </h2>
        <p className="text-[11px] text-white/40 mt-0.5">Actions taken by you and DiscoRun</p>
      </div>

      {/* Filter chips */}
      <div className="px-4 pt-3 pb-1 flex flex-wrap gap-1.5">
        {filterOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
              filter === opt.value
                ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30"
                : "bg-white/[0.04] text-white/40 border-white/[0.08] hover:bg-white/[0.07] hover:text-white/60"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <ScrollArea className="h-[600px]">
        <div className="px-4 py-4">
          {Object.keys(grouped).length === 0 && (
            <p className="text-center text-sm text-white/30 py-8">No activity yet</p>
          )}
          {Object.entries(grouped).map(([day, dayEvents]) => (
            <div key={day} className="mb-6">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/30 mb-3">
                {formatDayHeader(`${day}T00:00:00Z`)}
              </p>
              <div className="relative">
                {/* Vertical timeline line */}
                <div className="absolute left-[7px] top-0 bottom-0 w-px bg-white/[0.08]" />

                <div className="space-y-0">
                  {dayEvents.map((event) => (
                    <div key={event.id} className="relative flex gap-4 pb-5">
                      {/* Timeline dot */}
                      <div
                        className={`relative z-10 mt-1 w-[15px] h-[15px] rounded-full border-2 flex-shrink-0 ${getDotClass(event.type)}`}
                        style={{ borderColor: "hsl(224 40% 7%)" }}
                      />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="text-sm font-medium text-white/90 truncate">
                              {event.title}
                            </p>
                            <span className="text-[9px] px-1.5 py-0.5 rounded border border-white/[0.08] text-white/30 bg-white/[0.03] flex-shrink-0 flex items-center gap-0.5">
                              {event.actor === "discorun" ? (
                                <>
                                  <Bot className="w-2.5 h-2.5" />
                                  DiscoRun
                                </>
                              ) : (
                                "You"
                              )}
                            </span>
                          </div>
                          <span className="text-[11px] font-mono text-white/30 flex-shrink-0">
                            {formatTime(event.timestamp)}
                          </span>
                        </div>
                        {event.amount && (
                          <p className="text-sm font-mono tabular-nums text-indigo-400 mt-0.5">
                            {event.amount}
                          </p>
                        )}
                        {event.supplier &&
                          event.type !== "email_sent" &&
                          event.type !== "supplier_accepted" &&
                          event.type !== "supplier_declined" && (
                            <p className="text-xs text-white/40 mt-0.5">{event.supplier}</p>
                          )}
                        <p className="text-[11px] text-white/40 leading-relaxed mt-1">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
